import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";

const RD_FIELD_QUALIFICATION = "Qualificação (WhatsApp)";
const RD_FIELD_SOURCE = "Fonte (WhatsApp)";
const RD_FIELD_CAMPAIGN = "Campanha (WhatsApp)";
const RD_DEFAULT_PIPELINE = "Funil Padrão";

const findCustomFieldValue = (deal, label) => {
	const item = (deal?.deal_custom_fields || []).find(
		f => f.custom_field?.label === label
	);
	return item?.value || "";
};

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},

	qualificationPanel: {
		marginTop: 8,
		padding: 12,
		display: "flex",
		flexDirection: "column",
	},
	qualificationHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
	},
	qualificationField: {
		marginBottom: 12,
	},
	qualificationSaveButton: {
		alignSelf: "flex-end",
		position: "relative",
	},
	qualificationSaveProgress: {
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -10,
		marginLeft: -10,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [rdLoading, setRdLoading] = useState(false);
	const [rdDeal, setRdDeal] = useState(null);
	const [rdPipelines, setRdPipelines] = useState([]);
	const [rdUsers, setRdUsers] = useState([]);
	const [rdCustomFields, setRdCustomFields] = useState([]);
	const [rdSaving, setRdSaving] = useState(false);
	const [rdForm, setRdForm] = useState({
		qualification: "",
		source: "",
		campaign: "",
		funnelStageId: "",
		userId: "",
	});

	useEffect(() => {
		if (!contact.id || !contact.number) return;

		const fetchRdStation = async () => {
			setRdLoading(true);
			try {
				const [dealRes, pipelinesRes, usersRes, fieldsRes] = await Promise.all([
					api.get("/rdstation/deal", { params: { phone: contact.number } }),
					api.get("/rdstation/pipelines"),
					api.get("/rdstation/users"),
					api.get("/rdstation/custom-fields"),
				]);

				const deal = dealRes.data.deal;
				setRdDeal(deal);
				setRdPipelines(pipelinesRes.data.pipelines || []);
				setRdUsers(usersRes.data.users || []);
				setRdCustomFields(fieldsRes.data.customFields || []);
				setRdForm({
					qualification: findCustomFieldValue(deal, RD_FIELD_QUALIFICATION),
					source: findCustomFieldValue(deal, RD_FIELD_SOURCE),
					campaign: findCustomFieldValue(deal, RD_FIELD_CAMPAIGN),
					funnelStageId: deal?.deal_stage?.id || "",
					userId: deal?.user?.id || "",
				});
			} catch (err) {
				toastError(err);
			} finally {
				setRdLoading(false);
			}
		};

		fetchRdStation();
	}, [contact.id, contact.number]);

	const handleRdChange = field => event => {
		setRdForm(prev => ({ ...prev, [field]: event.target.value }));
	};

	const handleSaveRdDeal = async () => {
		setRdSaving(true);
		try {
			const payload = {
				phone: contact.number,
				qualification: rdForm.qualification,
				source: rdForm.source,
				campaign: rdForm.campaign,
				funnelStageId: rdForm.funnelStageId,
				userId: rdForm.userId,
			};
			const { data } = rdDeal
				? await api.put(`/rdstation/deals/${rdDeal.id}`, payload)
				: await api.post("/rdstation/deals", {
						...payload,
						name: contact.name,
				  });
			setRdDeal(data);
			toast.success(i18n.t("contactDrawer.qualification.saveSuccess"));
		} catch (err) {
			toastError(err);
		} finally {
			setRdSaving(false);
		}
	};

	const qualificationOptions =
		rdCustomFields.find(f => f.label === RD_FIELD_QUALIFICATION)?.opts || [];
	const sourceOptions =
		rdCustomFields.find(f => f.label === RD_FIELD_SOURCE)?.opts || [];
	const activePipeline =
		rdPipelines.find(p =>
			p.deal_stages?.some(s => s.id === rdForm.funnelStageId)
		) ||
		rdPipelines.find(p => p.name === RD_DEFAULT_PIPELINE) ||
		rdPipelines[0];
	const stageOptions = activePipeline?.deal_stages || [];

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography>{contact.name}</Typography>
						<Typography>
							<Link href={`tel:${contact.number}`}>{contact.number}</Link>
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>
					<Paper square variant="outlined" className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						<Typography variant="subtitle1">
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
					</Paper>
					<Paper square variant="outlined" className={classes.qualificationPanel}>
						<div className={classes.qualificationHeader}>
							<Typography variant="subtitle1" gutterBottom>
								{i18n.t("contactDrawer.qualification.title")}
							</Typography>
							{rdDeal && (
								<Link
									href={`https://crm.rdstation.com/app/deals/${rdDeal.id}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									{i18n.t("contactDrawer.qualification.openInRd")}
								</Link>
							)}
						</div>
						{rdLoading ? (
							<Typography variant="body2">
								{i18n.t("contactDrawer.qualification.loading")}
							</Typography>
						) : (
							<>
								{!rdDeal && (
									<Typography variant="body2" gutterBottom>
										{i18n.t("contactDrawer.qualification.noDeal")}
									</Typography>
								)}
								<FormControl
									variant="outlined"
									margin="dense"
									className={classes.qualificationField}
								>
									<InputLabel>
										{i18n.t("contactDrawer.qualification.qualification")}
									</InputLabel>
									<Select
										label={i18n.t("contactDrawer.qualification.qualification")}
										value={rdForm.qualification}
										onChange={handleRdChange("qualification")}
									>
										<MenuItem value="">
											{i18n.t("contactDrawer.qualification.options.empty")}
										</MenuItem>
										{qualificationOptions.map(option => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl
									variant="outlined"
									margin="dense"
									className={classes.qualificationField}
								>
									<InputLabel>
										{i18n.t("contactDrawer.qualification.source")}
									</InputLabel>
									<Select
										label={i18n.t("contactDrawer.qualification.source")}
										value={rdForm.source}
										onChange={handleRdChange("source")}
									>
										<MenuItem value="">
											{i18n.t("contactDrawer.qualification.options.empty")}
										</MenuItem>
										{sourceOptions.map(option => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									variant="outlined"
									margin="dense"
									className={classes.qualificationField}
									label={i18n.t("contactDrawer.qualification.campaign")}
									value={rdForm.campaign}
									onChange={handleRdChange("campaign")}
								/>
								<FormControl
									variant="outlined"
									margin="dense"
									className={classes.qualificationField}
								>
									<InputLabel>
										{i18n.t("contactDrawer.qualification.funnelStage")}
									</InputLabel>
									<Select
										label={i18n.t("contactDrawer.qualification.funnelStage")}
										value={rdForm.funnelStageId}
										onChange={handleRdChange("funnelStageId")}
									>
										{stageOptions.map(stage => (
											<MenuItem key={stage.id} value={stage.id}>
												{stage.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl
									variant="outlined"
									margin="dense"
									className={classes.qualificationField}
								>
									<InputLabel>
										{i18n.t("contactDrawer.qualification.responsible")}
									</InputLabel>
									<Select
										label={i18n.t("contactDrawer.qualification.responsible")}
										value={rdForm.userId}
										onChange={handleRdChange("userId")}
									>
										<MenuItem value="">
											{i18n.t("contactDrawer.qualification.options.empty")}
										</MenuItem>
										{rdUsers.map(user => (
											<MenuItem key={user.id} value={user.id}>
												{user.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<Button
									variant="contained"
									color="primary"
									disabled={rdSaving}
									className={classes.qualificationSaveButton}
									onClick={handleSaveRdDeal}
								>
									{rdDeal
										? i18n.t("contactDrawer.qualification.save")
										: i18n.t("contactDrawer.qualification.create")}
									{rdSaving && (
										<CircularProgress
											size={20}
											className={classes.qualificationSaveProgress}
										/>
									)}
								</Button>
							</>
						)}
					</Paper>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
